import { useRef, useState, useEffect, useCallback } from "react"
import { useCampaignContent } from "../campaign_content_context"
import {
  TrixEditorWrapper,
  type TrixEditorHandle,
  type ActiveAttributes,
} from "./trix_editor_wrapper"
import { EditorToolbar } from "./editor_toolbar"
import { EmailMetrics } from "./email_metrics"
import type { FollowUp } from "../types"
import { cn } from "~/lib/utils"

import { t } from "../i18n"

// Declare jQuery and App as global
declare const $: (selector: string) => JQuery
declare const App: {
  modal: (element: JQuery, action: "show" | "hide") => void
  enableForm: () => void
  displayFlash: (type: string, message: string) => void
}

interface JQuery {
  val: (value?: string) => string | JQuery
  is: (selector: string) => boolean
  trigger: (event: string) => JQuery
  unbind: (event: string) => JQuery
  on: (event: string, handler: (e?: Event) => boolean | void) => JQuery
  select: () => JQuery
  modal: (action: "show" | "hide") => void
}

interface EmailEditorCardProps {
  followUp: FollowUp
}

export function EmailEditorCard({ followUp }: EmailEditorCardProps) {
  const editorRef = useRef<TrixEditorHandle>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [activeAttributes, setActiveAttributes] = useState<ActiveAttributes>({
    bold: false,
    italic: false,
    strike: false,
  })

  const {
    campaign,
    editable,
    updateFollowUp,
    allowEmailAttachments,
    allowEmailImages,
    allowVideoLinks,
  } = useCampaignContent()

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateFollowUp(followUp.step, { subject: e.target.value })
  }

  const handleBodyChange = (body: string) => {
    updateFollowUp(followUp.step, { body })
  }

  const handleSelectionChange = (attributes: ActiveAttributes) => {
    setActiveAttributes(attributes)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      editorRef.current?.insertFile(file)
    }
    e.target.value = ""
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      editorRef.current?.insertFile(file)
    }
    e.target.value = ""
  }

  const handleInsertImage = () => {
    imageInputRef.current?.click()
  }

  const handleAttachFile = () => {
    fileInputRef.current?.click()
  }

  // Helper to prepend URL protocol if missing
  const prependUrlProtocol = (url: string): string => {
    if (!url.startsWith("{{")) {
      try {
        const urlObject = new URL(url)
        if (urlObject.protocol === null) {
          return "http://" + urlObject.href
        }
      } catch {
        return "http://" + url
      }
    }
    return url
  }

  // Helper to verify and insert attribute (prevents nested attributes)
  const verifyThenInsertAttribute = useCallback(
    (attributeString: string) => {
      const editor = editorRef.current
      if (!editor) return

      const editorElement = editor.getEditorElement()
      if (!editorElement) return

      const trixEditor = (
        editorElement as HTMLElement & { editor: TrixEditorInstance }
      ).editor
      if (!trixEditor) return

      const cursorPosition = trixEditor.getSelectedRange()[1]
      const textBeforeAttribute = trixEditor
        .getDocument()
        .getStringAtRange([0, cursorPosition])
      const openingBracketsCount = (textBeforeAttribute.match(/{{/g) || [])
        .length
      const closingBracketsCount = (textBeforeAttribute.match(/}}/g) || [])
        .length
      const attributeLength = attributeString.length

      if (openingBracketsCount === closingBracketsCount) {
        trixEditor.insertString(attributeString)
        trixEditor.setSelectedRange([
          cursorPosition,
          cursorPosition + attributeLength,
        ])
        trixEditor.activateAttribute("customAttr")
      } else {
        App.displayFlash("danger", "Nested attributes aren't supported")
      }
    },
    [],
  )

  // Handle link insertion using the legacy modal
  const handleInsertLink = useCallback(() => {
    const editor = editorRef.current
    if (!editor) return

    const editorElement = editor.getEditorElement()
    if (!editorElement) return

    const trixEditor = (
      editorElement as HTMLElement & { editor: TrixEditorInstance }
    ).editor
    if (!trixEditor) return

    const modal = $("#href-modal")

    // Reset to URL tab
    $("[aria-controls='link-form-url']").trigger("click")
    $("#href-field").val("")
    $("#link-attribute-identifier").val("")

    App.modal(modal, "show")

    // Unbind previous handlers and bind new one
    $("#href-button").unbind("click")
    $("#href-button").on("click", function () {
      try {
        if ($("#link-form-url").is(":visible")) {
          const url = $("#href-field").val() as string
          trixEditor.activateAttribute("href", prependUrlProtocol(url.trim()))
        } else if ($("#link-form-attributes").is(":visible")) {
          const attributeString = `{{${$("#link-attribute-identifier").val()}}}`
          const cursorPosition = trixEditor.getSelectedRange()[1]
          const attributeLength = attributeString.length

          trixEditor.activateAttribute("href", attributeString)
          trixEditor.setSelectedRange([
            cursorPosition,
            cursorPosition + attributeLength,
          ])
          trixEditor.activateAttribute("customAttr")
        }

        App.enableForm()
        App.modal($("#href-modal"), "hide")
      } catch (error) {
        console.error("Error in href button click handler:", error)
        App.modal($("#href-modal"), "hide")
      }
      return false
    })

    // Select the URL field after modal opens
    setTimeout(() => {
      $("#href-field").select()
    }, 100)
  }, [])

  // Handle attribute insertion using the legacy modal
  const handleInsertAttribute = useCallback(() => {
    App.modal($("#attribute-modal"), "show")

    setTimeout(() => {
      $("#attribute-identifier").val("first_name")
      $("#attribute-fallback").val("")

      $("#attribute-form")
        .unbind("submit")
        .on("submit", function () {
          const attributeString = `{{${$("#attribute-identifier").val()}:"${$("#attribute-fallback").val()}"}}`

          verifyThenInsertAttribute(attributeString)

          App.enableForm()
          App.modal($("#attribute-modal"), "hide")
          return false
        })
    }, 300)
  }, [verifyThenInsertAttribute])

  // Handle video insertion using the legacy modal
  const handleInsertVideo = useCallback(() => {
    // Dispatch the event that the trix-video controller listens for
    const event = new CustomEvent("trix-editor:videoModalOpen", {
      bubbles: true,
      detail: {
        campaignId: campaign.id,
        followUpId: followUp.id,
        attachmentId: null,
        videoUrl: "",
        thumbnailUrl: "",
      },
    })
    window.dispatchEvent(event)
  }, [campaign.id, followUp.id])

  // Listen for video add events from the video modal
  useEffect(() => {
    const handleVideoAdd = (
      event: CustomEvent<{
        followUpId: number
        thumbnailHtml: string
        videoUrl: string
        thumbnailUrl: string
      }>,
    ) => {
      // Check if this event is for our follow-up
      if (event.detail.followUpId && event.detail.followUpId !== followUp.id) {
        return
      }

      const editor = editorRef.current
      if (!editor) return

      const editorElement = editor.getEditorElement()
      if (!editorElement) return

      const trixEditor = (
        editorElement as HTMLElement & { editor: TrixEditorInstance }
      ).editor
      if (!trixEditor) return

      // Create and insert video attachment
      // @ts-expect-error Trix is a global
      const attachment = new window.Trix.Attachment({
        type: "video",
        contentType: "image/jpeg",
        content: event.detail.thumbnailHtml.trim(),
        videoUrl: event.detail.videoUrl,
        thumbnailUrl: event.detail.thumbnailUrl,
      })
      trixEditor.insertAttachment(attachment)
    }

    window.addEventListener(
      "trix-video:trix-video-add" as keyof WindowEventMap,
      handleVideoAdd as EventListener,
    )

    return () => {
      window.removeEventListener(
        "trix-video:trix-video-add" as keyof WindowEventMap,
        handleVideoAdd as EventListener,
      )
    }
  }, [followUp.id])

  const isOpeningEmail = followUp.step === 0

  return (
    <div className="compose-message">
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: "none" }}
      />
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        style={{ display: "none" }}
      />

      <div className="compose-message-subject">
        <label
          className="compose-message-subject__label"
          htmlFor={`subject-field-${followUp.step}`}
        >
          {t("js.campaigns.content.subject")}:
        </label>
        <input
          id={`subject-field-${followUp.step}`}
          className={cn(
            "compose-message-subject__input",
            followUp.subject && "compose-message-subject__input--filled",
          )}
          type="text"
          value={followUp.subject}
          onChange={handleSubjectChange}
          placeholder={
            isOpeningEmail
              ? t("js.campaigns.content.subject_placeholder")
              : t("js.campaigns.content.follow_up_subject_placeholder")
          }
          disabled={!editable}
          required={isOpeningEmail}
        />
      </div>

      <div className="compose-message-body">
        <TrixEditorWrapper
          ref={editorRef}
          id={`trix-editor-${followUp.id}`}
          value={followUp.body}
          onChange={handleBodyChange}
          onSelectionChange={handleSelectionChange}
          placeholder={t("js.campaigns.content.compose_email_placeholder")}
          disabled={!editable}
        />
      </div>

      {editable && (
        <EditorToolbar
          onToggleBold={() => editorRef.current?.toggleBold()}
          onToggleItalic={() => editorRef.current?.toggleItalic()}
          onToggleStrike={() => editorRef.current?.toggleStrike()}
          onToggleBulletList={() => editorRef.current?.toggleBulletList()}
          onToggleNumberedList={() => editorRef.current?.toggleNumberedList()}
          onInsertLink={handleInsertLink}
          onInsertImage={handleInsertImage}
          onInsertVideo={handleInsertVideo}
          onInsertAttribute={handleInsertAttribute}
          onAttachFile={handleAttachFile}
          allowEmailAttachments={allowEmailAttachments}
          allowEmailImages={allowEmailImages}
          allowVideoLinks={allowVideoLinks}
          isBoldActive={activeAttributes.bold}
          isItalicActive={activeAttributes.italic}
          isStrikeActive={activeAttributes.strike}
          disabled={!editable}
        />
      )}

      <EmailMetrics body={followUp.body} subject={followUp.subject} />
    </div>
  )
}

// Type for the Trix editor instance
interface TrixEditorInstance {
  loadHTML: (html: string) => void
  getDocument: () => { toString: () => string; getStringAtRange: (range: [number, number]) => string }
  activateAttribute: (name: string, value?: string) => void
  deactivateAttribute: (name: string) => void
  attributeIsActive: (name: string) => boolean
  insertString: (text: string) => void
  insertFile: (file: File) => void
  insertAttachment: (attachment: unknown) => void
  getSelectedRange: () => [number, number]
  setSelectedRange: (range: [number, number]) => void
  composition: {
    insertString: (text: string) => void
  }
}
